import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ReplaySubject, Subject, startWith, takeUntil } from "rxjs";
import { Area } from "src/app/shared/model/tickets/area.model";
import { Formulario } from "src/app/shared/model/tickets/formulario";
import { Tickets } from "src/app/shared/model/tickets/tickets.model";
import { ComunicacionService } from "src/app/shared/services/comunicacion.service";
import { Constantes } from "src/app/utilerias/constantes/constantes";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import { TicketsEstatus } from "src/app/utilerias/enums/tickets-estatus.enum";
import { TicketsService } from "../tickets.service";
import {
  ColumnaTicketsBaseComponent,
  TicketPaginationState,
} from "./columna-tickets-base.component";

export interface TicketBoardColumn {
  titulo: string;
  icon: string;
  color: string;
  estatus: TicketsEstatus;
}

export interface TicketBoardPaginationConfig {
  id: string;
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  totalPages: number;
}

/** Lo que cambia entre los dos tableros en la plantilla que comparten. */
export interface VistaTableroTickets {
  /**
   * Clase de la página (tickets-client-page o tickets-admin-page) y prefijo de sus clases propias. La
   * plantilla la agrega con [class] después de las clases fijas de cada elemento: el atributo class queda
   * con las clases en el mismo orden que tenía cada plantilla.
   */
  clasePagina: string;
  /** Ícono y texto del botón principal de la barra, que llama a accionPrincipal(). */
  iconoAccion: string;
  textoAccion: string;
  /**
   * Estilo secundario del botón principal. La plantilla trae table-page__icon-btn--secondary fija, en su
   * lugar entre las demás clases; con false se quita y el botón queda primario.
   */
  accionSecundaria: boolean;
  /** Columnas del cliente (app-notificacion-cte-ticket) o de atención (app-notificacion-ticket). */
  columnasCliente: boolean;
  /** Mensaje cuando no se pudieron cargar las áreas del tablero. */
  textoSinAreas: string;
}

/**
 * Base de los tableros kanban de tickets: el del cliente (GestionCteTicketsComponent, /TICKETS/Cte)
 * y el de atención (GestionTicketsComponent, /TICKETS/Admin). Aquí están las columnas, los filtros,
 * la paginación por columna y el arrastre del tablero. Cada tablero dice qué áreas ofrece y cuál
 * deja elegida, qué columnas pinta (su componente de columna) y el prefijo de su paginación.
 * Los dos pintan la plantilla de GestionCteTicketsComponent: lo que cambia entre ellos está en
 * vista y en accionPrincipal().
 */
@Directive()
export abstract class TableroTicketsBaseComponent implements AfterViewInit, OnInit, OnDestroy {
  protected readonly destroy$ = new Subject<void>();
  private readonly defaultPageSize = 5;
  private pendingBoardRefresh = false;

  readonly columnasKanban: TicketBoardColumn[] = [
    {
      titulo: "Abiertas",
      icon: "mark_email_unread",
      color: Constantes.ABIERTOCOLOR,
      estatus: TicketsEstatus.ABIERTA,
    },
    {
      titulo: "Proceso",
      icon: "settings_applications",
      color: Constantes.PROCESOCOLOR,
      estatus: TicketsEstatus.PROCESO,
    },
    {
      titulo: "Atendidas",
      icon: "task_alt",
      color: Constantes.RESUELTOCOLOR,
      estatus: TicketsEstatus.ATENDIDA,
    },
    {
      titulo: "Vo.Bo.",
      icon: "verified",
      color: Constantes.VOBOCOLOR,
      estatus: TicketsEstatus.VOBO,
    },
    {
      titulo: "Canceladas",
      icon: "cancel",
      color: Constantes.CANCELADOCOLOR,
      estatus: TicketsEstatus.CANCELADA,
    },
  ];

  readonly pageSizeOptions = [5, 10, 25, 100];
  private readonly totalColumns = this.columnasKanban.length;

  formulario = new Formulario();
  options!: FormGroup;
  date = new FormControl<Date | null>(null);
  areas: Area[] = [];
  areaSeleccionada: Area | null = null;
  nombreCorto = "";
  fechaSeleccionada: Date | null = null;
  numero: number | null = null;
  itemsPerPage = this.defaultPageSize;
  paginationConfigs = this.createPaginationConfigs();
  pendingRefreshByStatus: Record<number, boolean> = {
    [TicketsEstatus.ABIERTA]: false,
    [TicketsEstatus.PROCESO]: false,
    [TicketsEstatus.ATENDIDA]: false,
    [TicketsEstatus.VOBO]: false,
    [TicketsEstatus.CANCELADA]: false,
  };

  formArea = new FormControl("");
  filterArea: ReplaySubject<Area[]> = new ReplaySubject<Area[]>(1);
  filtersPopupOpen = false;
  isDraggingBoard = false;

  private dragStartX = 0;
  private dragStartScrollLeft = 0;

  /** Textos, clases y columnas propios de cada tablero en la plantilla compartida. */
  abstract readonly vista: VistaTableroTickets;
  /** Ticket que el tablero de atención pasa a sus columnas ([ticket]); el del cliente lo deja sin valor. */
  ticket?: Tickets;
  /** Columnas del tablero: cada tablero la declara con @ViewChildren de su componente de columna. */
  abstract ticketBoards: QueryList<ColumnaTicketsBaseComponent>;
  @ViewChild("boardScroller") boardScroller?: ElementRef<HTMLDivElement>;
  @ViewChild("filtersButton", { read: ElementRef })
  filtersButton?: ElementRef<HTMLElement>;
  @ViewChild("filtersPopover")
  filtersPopover?: ElementRef<HTMLDivElement>;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    protected readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    protected readonly ticketsService: TicketsService,
    private readonly comunicacion: ComunicacionService
  ) {}

  /**
   * Prefijo de los ids de paginación de cada columna. Es un getter para que ya responda
   * mientras se inicializan los campos de esta base.
   */
  protected abstract get prefijoPaginacion(): string;

  /** Con el catálogo de áreas y las del usuario, cada tablero decide cuáles ofrece y cuál deja elegida. */
  protected abstract asignarAreas(areasCatalogo: Area[], areasUsuario: Area[]): void;

  /** Botón principal de la barra: el cliente solicita un ticket y atención abre los reportes. */
  abstract accionPrincipal(): void;

  ngOnInit(): void {
    this.formulario.mostrarFrmControl = true;
    this.options = this.fb.group({
      areaCtrl: [null],
      asuntoCtrl: [""],
      numeroCtrl: [""],
      dateCtrl: [null],
    });

    this.formArea.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.filtraAreas();
    });

    this.suscribirAvisosDeCambio();
    this.cargarAreasDisponibles();
  }

  /**
   * Avisos de tickets que cambiaron: marcan para recargar las columnas que tocan. El tablero del
   * cliente lo amplía con los avisos del detalle y de sus tickets nuevos.
   */
  protected suscribirAvisosDeCambio(): void {
    this.comunicacion.actualizar.pipe(takeUntil(this.destroy$)).subscribe((ticketActualizado) => {
      if (ticketActualizado) {
        this.marcarColumnasPendientes(ticketActualizado);
      }
    });
  }

  ngAfterViewInit(): void {
    this.ticketBoards.changes
      .pipe(startWith(this.ticketBoards), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.pendingBoardRefresh) {
          this.tryRefreshAll();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get boardTotalItems(): number {
    return this.paginationConfigs.reduce(
      (accumulator, config) => accumulator + Number(config?.totalItems ?? 0),
      0
    );
  }

  trackByAreaId(_index: number, area: Area): number {
    return area.id;
  }

  trackByColumn(_index: number, column: TicketBoardColumn): number {
    return column.estatus;
  }

  onItemsPerPageChange(value: number | string): void {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed === this.itemsPerPage) {
      return;
    }

    this.itemsPerPage = parsed;
    this.paginationConfigs.forEach((config) => {
      config.itemsPerPage = parsed;
      config.currentPage = 1;
      config.totalItems = 0;
      config.totalPages = 0;
    });
    this.deferRefreshAll();
  }

  onAreaChange(area: Area | null): void {
    this.areaSeleccionada = area;
    this.resetPagination();
    this.deferRefreshAll();
  }

  actualizaTickets(): void {
    this.resetPendingRefreshFlags();
    this.deferRefreshAll();
  }

  activafiltros(event?: MouseEvent): void {
    event?.stopPropagation();

    if (this.filtersPopupOpen) {
      this.cerrarFiltrosPopup();
      return;
    }

    this.filtersPopupOpen = true;
  }

  cerrarFiltrosPopup(): void {
    this.filtersPopupOpen = false;
  }

  limpiaFiltrosPopup(): void {
    this.options.patchValue({
      asuntoCtrl: "",
      numeroCtrl: "",
      dateCtrl: null,
    });
    this.nombreCorto = "";
    this.numero = null;
    this.fechaSeleccionada = null;
    this.date.setValue(null, { emitEvent: false });
    this.resetPagination();
    this.deferRefreshAll();
  }

  aplicarFiltros(): void {
    const asunto = this.options.get("asuntoCtrl")?.value ?? "";
    const numeroControl = this.options.get("numeroCtrl")?.value;
    const fechaControl = this.options.get("dateCtrl")?.value ?? null;
    const numero = Number(numeroControl);

    this.nombreCorto = String(asunto).trim();
    this.numero = Number.isFinite(numero) && numero > 0 ? numero : null;
    this.fechaSeleccionada = fechaControl ? new Date(fechaControl) : null;
    this.date.setValue(this.fechaSeleccionada, { emitEvent: false });

    this.resetPagination();
    this.deferRefreshAll();
    this.cerrarFiltrosPopup();
  }

  getActiveFiltersCount(): number {
    let count = 0;

    if (this.nombreCorto.trim()) {
      count++;
    }
    if (this.fechaSeleccionada) {
      count++;
    }
    if (this.numero) {
      count++;
    }

    return count;
  }

  setPaginacionState(state: TicketPaginationState): void {
    const config = this.getColumnConfig(state.position);
    if (!config) {
      return;
    }

    config.totalItems = Math.max(state.totalItems, 0);
    config.totalPages = Math.max(state.totalPages, config.totalItems > 0 ? 1 : 0);
    this.paginationConfigs = [...this.paginationConfigs];

    if (config.totalPages <= 0) {
      config.currentPage = 1;
      this.cdr.detectChanges();
      return;
    }

    if (config.currentPage > config.totalPages) {
      config.currentPage = config.totalPages;
      this.refrescarColumna(state.position);
      return;
    }

    this.cdr.detectChanges();
  }

  setLegacyPaginacionState(event: any[]): void {
    const totalPages = Number(event?.[0] ?? 0);
    const position = Number(event?.[1] ?? -1);
    const totalItems = Number(event?.[2] ?? 0);

    if (!Number.isFinite(position) || position < 0 || position >= this.totalColumns) {
      return;
    }

    this.setPaginacionState({
      position,
      totalItems: Number.isFinite(totalItems) && totalItems >= 0 ? totalItems : 0,
      totalPages: Number.isFinite(totalPages) && totalPages >= 0 ? totalPages : 0,
    });
  }

  getColumnTotalItems(index: number): number {
    const config = this.getColumnConfig(index);
    const childItems = this.ticketBoards?.toArray?.()?.[index]?.list?.length ?? 0;
    return Math.max(Number(config?.totalItems ?? 0), Number(childItems ?? 0));
  }

  getColumnConfig(index: number): TicketBoardPaginationConfig | null {
    return this.paginationConfigs[index] ?? null;
  }

  getTotalPages(config: TicketBoardPaginationConfig | null): number {
    if (!config) {
      return 0;
    }

    const totalItems = Number(config.totalItems ?? 0);
    const itemsPerPage = Number(config.itemsPerPage ?? this.itemsPerPage);
    const derivedTotalPages =
      totalItems > 0 && itemsPerPage > 0 ? Math.ceil(totalItems / itemsPerPage) : 0;

    return Math.max(Number(config.totalPages ?? 0), derivedTotalPages);
  }

  getPaginationItems(config: TicketBoardPaginationConfig | null): Array<number | string> {
    const totalPages = this.getTotalPages(config);
    const currentPage = Number(config?.currentPage ?? 1);

    if (totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((left, right) => left - right);

    const items: Array<number | string> = [];
    for (let indexPage = 0; indexPage < sortedPages.length; indexPage++) {
      const page = sortedPages[indexPage];
      const previousPage = sortedPages[indexPage - 1];

      if (previousPage && page - previousPage > 1) {
        items.push(`ellipsis-${previousPage}-${page}`);
      }

      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  firstPage(index: number): void {
    this.changeColumnPage(index, 1);
  }

  previousPage(index: number): void {
    const config = this.getColumnConfig(index);
    this.changeColumnPage(index, Number(config?.currentPage ?? 1) - 1);
  }

  nextPage(index: number): void {
    const config = this.getColumnConfig(index);
    this.changeColumnPage(index, Number(config?.currentPage ?? 1) + 1);
  }

  lastPage(index: number): void {
    this.changeColumnPage(index, this.getTotalPages(this.getColumnConfig(index)));
  }

  pageChanged(page: number, index: number): void {
    this.changeColumnPage(index, page);
  }

  recargarColumna(column: TicketBoardColumn, index: number): void {
    this.pendingRefreshByStatus[column.estatus] = false;
    this.refrescarColumna(index);
  }

  hasPendingRefresh(estatus: TicketsEstatus): boolean {
    return !!this.pendingRefreshByStatus[estatus];
  }

  startBoardDrag(event: MouseEvent): void {
    if (!this.boardScroller) {
      return;
    }

    this.isDraggingBoard = true;
    this.dragStartX = event.pageX;
    this.dragStartScrollLeft = this.boardScroller.nativeElement.scrollLeft;
  }

  moveBoardDrag(event: MouseEvent): void {
    if (!this.isDraggingBoard || !this.boardScroller) {
      return;
    }

    const displacement = event.pageX - this.dragStartX;
    this.boardScroller.nativeElement.scrollLeft = this.dragStartScrollLeft - displacement;
  }

  stopBoardDrag(): void {
    this.isDraggingBoard = false;
  }

  @HostListener("document:click", ["$event"])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.filtersPopupOpen) {
      return;
    }

    const target = event.target as Node | null;
    const clickedInsidePopover = !!(
      target && this.filtersPopover?.nativeElement?.contains(target)
    );
    const clickedButton = !!(
      target && this.filtersButton?.nativeElement?.contains(target)
    );

    if (!clickedInsidePopover && !clickedButton) {
      this.cerrarFiltrosPopup();
    }
  }

  @HostListener("document:keydown.escape")
  handleEscapeKey(): void {
    if (this.filtersPopupOpen) {
      this.cerrarFiltrosPopup();
    }
  }

  protected marcarColumnasPendientes(ticketActualizado: Tickets): void {
    const estatusNuevo = ticketActualizado.id_estatus;
    const estatusPrevio = ticketActualizado.id_estatusPrevio;

    this.columnasKanban.forEach((column) => {
      if (column.estatus === TicketsEstatus.ABIERTA) {
        const touchedOpenColumn =
          estatusNuevo === TicketsEstatus.ABIERTA ||
          estatusNuevo === TicketsEstatus.REABIERTA ||
          estatusPrevio === TicketsEstatus.ABIERTA ||
          estatusPrevio === TicketsEstatus.REABIERTA;
        if (touchedOpenColumn) {
          this.pendingRefreshByStatus[column.estatus] = true;
        }
        return;
      }

      if (column.estatus === estatusNuevo || column.estatus === estatusPrevio) {
        this.pendingRefreshByStatus[column.estatus] = true;
      }
    });
  }

  protected deferRefreshAll(): void {
    this.pendingBoardRefresh = true;
    setTimeout(() => {
      this.tryRefreshAll();
    });
  }

  private changeColumnPage(index: number, page: number): void {
    const config = this.getColumnConfig(index);
    const totalPages = this.getTotalPages(config);

    if (!config || totalPages <= 0 || page < 1 || page > totalPages || page === config.currentPage) {
      return;
    }

    config.currentPage = page;
    this.paginationConfigs = [...this.paginationConfigs];
    this.refrescarColumna(index);
  }

  private cargarAreasDisponibles(): void {
    this.ticketsService
      .consultarArea(UrlConstantes.consultarArea)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        const areasCatalogo = Array.isArray(res?.list) ? res.list : [];
        if (!areasCatalogo.length) {
          this.areas = [];
          this.filterArea.next([]);
          return;
        }

        this.ticketsService
          .consultarArea(UrlConstantes.consultarAreaUser)
          .pipe(takeUntil(this.destroy$))
          .subscribe((res2) => {
            const areasUsuario = Array.isArray(res2?.list) ? res2.list : [];
            this.asignarAreas(areasCatalogo, areasUsuario);
          });
      });
  }

  private filtraAreas(): void {
    if (!this.areas.length) {
      this.filterArea.next([]);
      return;
    }

    let search = this.formArea.value;
    if (!search) {
      this.filterArea.next(this.areas.slice());
      return;
    }

    search = String(search).toLowerCase();
    this.filterArea.next(
      this.areas.filter((area) => String(area.area).toLowerCase().includes(search))
    );
  }

  private resetPendingRefreshFlags(): void {
    Object.keys(this.pendingRefreshByStatus).forEach((key) => {
      this.pendingRefreshByStatus[Number(key)] = false;
    });
  }

  private resetPagination(): void {
    this.paginationConfigs = this.createPaginationConfigs();
    this.cdr.detectChanges();
  }

  private tryRefreshAll(): void {
    if (!this.areaSeleccionada || !this.hasReadyChildren()) {
      return;
    }

    this.pendingBoardRefresh = false;
    this.refrescarTodasLasColumnas();
    this.cdr.detectChanges();
  }

  private hasReadyChildren(): boolean {
    return !!this.ticketBoards && this.ticketBoards.length >= this.totalColumns;
  }

  private refrescarTodasLasColumnas(): void {
    this.columnasKanban.forEach((_column, index) => {
      this.refrescarColumna(index);
    });
  }

  private refrescarColumna(index: number): void {
    const child = this.ticketBoards?.toArray()?.[index];
    const config = this.getColumnConfig(index);
    if (!child || !this.areaSeleccionada || !config) {
      return;
    }

    child.inicializarValoresPaginacion(
      this.nombreCorto,
      this.fechaSeleccionada,
      this.areaSeleccionada,
      this.calculaRegistros(config),
      config.itemsPerPage,
      index,
      this.numero
    );
    child.llamado();
  }

  private calculaRegistros(config: TicketBoardPaginationConfig): number {
    const currentPage = Number(config.currentPage ?? 1);
    const itemsPerPage = Number(config.itemsPerPage ?? this.itemsPerPage);
    return currentPage <= 1 ? 0 : (currentPage - 1) * itemsPerPage;
  }

  private createPaginationConfigs(): TicketBoardPaginationConfig[] {
    return this.columnasKanban.map((column, index) => ({
      id: `${this.prefijoPaginacion}-${column.estatus}-${index}`,
      currentPage: 1,
      itemsPerPage: this.itemsPerPage,
      totalItems: 0,
      totalPages: 0,
    }));
  }
}
